import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { TextPropsOptions, TextPropsTextConfig } from '../types';
import { TAGS_METADATA } from '@features/powerpoint/utils/tags';

type ValidTag = (typeof TAGS_METADATA)[number]['tag'];

const metadataByTag = TAGS_METADATA.reduce(
  (acc, tagMetadata) => {
    acc[tagMetadata.tag] = tagMetadata;
    return acc;
  },
  {} as Record<ValidTag, (typeof TAGS_METADATA)[number]>
);

type ParsedText = {
  text: string;
  tags?: ValidTag[];
};

const allowedTags = Object.keys(metadataByTag);

const isValidTag = (tag: string): tag is ValidTag => {
  return allowedTags.includes(tag);
};

export const parseAst = (input: string): ParsedText[] => {
  const tagRegex = /<([^>]+)>(.*?)<\/\1>/gs;

  let match;
  const result: ParsedText[] = [];
  let lastIndex = 0;

  while ((match = tagRegex.exec(input)) !== null) {
    const [_, tag = '', content = ''] = match;

    if (!isValidTag(tag)) {
      continue;
    }

    if (match.index > lastIndex) {
      result.push({ text: input.slice(lastIndex, match.index) });
    }

    const nested = parseAst(content);
    if (nested.length > 0 && nested[0]?.tags) {
      nested[0].tags.push(tag);
    } else {
      const firstNested = nested[0];
      if (firstNested) {
        firstNested.tags = [tag];
      }
    }
    result.push(...nested);

    lastIndex = tagRegex.lastIndex;
  }

  if (lastIndex < input.length) {
    result.push({ text: input.slice(lastIndex) });
  }

  return result;
};

const assertNever = (x: never): never => {
  throw new Error('Unexpected object:', x);
};

export const parseTags = (
  input: string,
  prependOptions?: TextPropsOptions
): TextPropsTextConfig[] => {
  const ast = parseAst(input);

  return ast.map(({ text, tags }, index) => {
    const options = tags?.reduce((acc, tag) => {
      switch (tag) {
        case 'b': {
          return { ...acc, bold: true };
        }
        case 'i': {
          return { ...acc, italic: true };
        }
        case 'u': {
          return { ...acc, underline: { style: 'sng' as const } };
        }
        case 'sup': {
          return { ...acc, superscript: true };
        }
        case 'lci': {
          return {
            ...acc,
            bold: true,
            color: unifiedPlatformColorPalette.inmarketGreen,
          };
        }
        case 'vr': {
          return {
            ...acc,
            bold: true,
            color: unifiedPlatformColorPalette.lightBlue,
          };
        }
        default: {
          assertNever(tag); // hack for exhaustiveness check. If all tag cases are properly accounted for, tag will be never.
          return acc;
        }
      }
    }, {} as TextPropsOptions);
    return {
      text,
      options: {
        ...options,
        ...(index === 0 && prependOptions ? prependOptions : {}),
        ...(index === ast.length - 1 ? { breakLine: true } : {}),
      },
    };
  });
};
